export type EpisodeQueuePageStatus =
  | 'pending'
  | 'running'
  | 'awaiting_selection'
  | 'done'
  | 'failed'
  | 'cancelled'

export type EpisodeGenerateQueueState = {
  episodeId: string
  episodeTitle: string
  pageIds: string[]
  pageTitles: Record<string, string>
  statuses: Record<string, EpisodeQueuePageStatus>
  currentPageId: string | null
  cancelled: boolean
  /** 页级预览图（0~n 张，如整页候选）；详情区按选中页展示 */
  pagePreviewUrls?: Record<string, string[]>
}

export function createEpisodeGenerateQueue(
  episodeId: string,
  episodeTitle: string,
  pageIds: string[],
  pageTitles: Record<string, string>,
): EpisodeGenerateQueueState {
  const statuses: Record<string, EpisodeQueuePageStatus> = {}
  for (const id of pageIds) statuses[id] = 'pending'
  return {
    episodeId,
    episodeTitle,
    pageIds,
    pageTitles,
    statuses,
    currentPageId: null,
    cancelled: false,
  }
}

export type EpisodeQueueSummary = {
  total: number
  succeeded: number
  failed: number
  cancelled: number
  awaiting: number
  pending: number
  running: number
  /** 无进行中/等待页，且各页均已进入终态 */
  finished: boolean
}

export function summarizeEpisodeQueue(state: EpisodeGenerateQueueState): EpisodeQueueSummary {
  let succeeded = 0
  let failed = 0
  let cancelled = 0
  let awaiting = 0
  let pending = 0
  let running = 0
  for (const id of state.pageIds) {
    switch (state.statuses[id]) {
      case 'done':
        succeeded += 1
        break
      case 'failed':
        failed += 1
        break
      case 'cancelled':
        cancelled += 1
        break
      case 'awaiting_selection':
        awaiting += 1
        break
      case 'running':
        running += 1
        break
      default:
        pending += 1
    }
  }
  const total = state.pageIds.length
  const finished =
    !state.cancelled &&
    running === 0 &&
    pending === 0 &&
    succeeded + failed + cancelled + awaiting >= total
  return { total, succeeded, failed, cancelled, awaiting, pending, running, finished }
}

/** @deprecated 使用 summarizeEpisodeQueue；done 仅含 succeeded+cancelled */
export function queueProgress(state: EpisodeGenerateQueueState): {
  total: number
  done: number
  failed: number
  awaiting: number
} {
  const s = summarizeEpisodeQueue(state)
  return {
    total: s.total,
    done: s.succeeded + s.cancelled,
    failed: s.failed,
    awaiting: s.awaiting,
  }
}

export function formatEpisodeQueueCompletionToast(
  episodeTitle: string,
  queue: EpisodeGenerateQueueState | null,
): { message: string; variant: 'success' | 'warning' | 'error' } | null {
  if (!queue) return null
  if (queue.cancelled) {
    return { message: '已取消生成本话队列', variant: 'warning' }
  }
  const s = summarizeEpisodeQueue(queue)
  if (!s.finished) return null
  if (s.awaiting > 0) {
    return {
      message: `${episodeTitle}：${s.succeeded}/${s.total} 页已完成，${s.awaiting} 页待选定`,
      variant: 'warning',
    }
  }
  if (s.failed > 0) {
    return {
      message: `${episodeTitle}：完成 ${s.succeeded}/${s.total} 页，${s.failed} 页失败`,
      variant: 'warning',
    }
  }
  if (s.succeeded > 0) {
    return {
      message: `${episodeTitle}：已全部完成（${s.succeeded} 页）`,
      variant: 'success',
    }
  }
  return { message: `${episodeTitle}：队列已结束`, variant: 'warning' }
}
